import React from 'react';
import { flexRender, Row, Table as ReactTable } from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

interface DataTableProps<TData> {
  table: ReactTable<TData>;
  onRowClick?: (row: TData) => void;
  onRowContextMenu?: (e: React.MouseEvent, row: TData) => void;
  className?: string;
  isLoading?: boolean;
  emptyMessage?: string;
  emptyState?: React.ReactNode;
  groupHeaderFn?: (row: Row<TData>, index: number) => React.ReactNode | null;
  getRowClassName?: (row: Row<TData>) => string | undefined;
}

export function DataTable<TData>({
  table,
  onRowClick,
  onRowContextMenu,
  className,
  isLoading = false,
  emptyMessage = 'No results.',
  emptyState,
  groupHeaderFn,
  getRowClassName,
}: DataTableProps<TData>) {
  const columns = table.getAllColumns();

  return (
    <div className={cn('rounded-md border overflow-auto', className)}>
      <Table>
        <TableHeader sticky>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    style={{
                      width: header.getSize(),
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                Loading...
              </TableCell>
            </TableRow>
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row, index) => {
              const groupHeader = groupHeaderFn?.(row, index) ?? null;
              return (
                <React.Fragment key={row.id}>
                  {groupHeader !== null && (
                    <TableRow className="bg-muted hover:bg-muted border-t-2 border-t-border">
                      <TableCell colSpan={columns.length} className="py-2">
                        {groupHeader}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow
                    data-state={row.getIsSelected() && 'selected'}
                    onClick={() => onRowClick?.(row.original)}
                    onContextMenu={
                      onRowContextMenu
                        ? (e) => {
                            e.preventDefault();
                            onRowContextMenu(e, row.original);
                          }
                        : undefined
                    }
                    className={cn(
                      onRowClick && 'cursor-pointer',
                      getRowClassName?.(row),
                    )}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                </React.Fragment>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                {emptyState ?? emptyMessage}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
